import { useState } from 'react';
import type { FormEvent } from 'react';
import { useAuth } from '../hooks/useAuth.js';
import { updateProfile, ApiError } from '../api/client.js';
import { formatPhone } from '../utils/phone.js';

export function AccountPage() {
  const { user, refreshUser } = useAuth();

  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [phone, setPhone] = useState(user?.phone ?? '');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSuccess('');
    setError('');

    try {
      await updateProfile({ name, email, phone });
      await refreshUser();
      setSuccess('Profile updated successfully');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const inputClasses =
    'w-full rounded-lg border border-gray-700 bg-gray-800 px-4 py-2.5 text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';

  return (
    <div className="animate-fade-in">
      <h1 className="mb-1 text-2xl font-bold text-gray-100">Account</h1>
      <p className="mb-6 text-sm text-gray-400">Manage your profile information</p>

      <div className="max-w-md">
        {success && (
          <div className="mb-4 rounded-lg bg-green-900/30 px-4 py-3 text-sm text-green-400">
            {success}
          </div>
        )}

        {error && (
          <div className="mb-4 rounded-lg bg-red-900/30 px-4 py-3 text-sm text-red-400">
            {error}
          </div>
        )}

        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label htmlFor="account-name" className="mb-1 block text-sm text-gray-400">
              Name
            </label>
            <input
              id="account-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputClasses}
              required
            />
          </div>

          <div>
            <label htmlFor="account-email" className="mb-1 block text-sm text-gray-400">
              Email
            </label>
            <input
              id="account-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClasses}
              required
            />
          </div>

          <div>
            <label htmlFor="account-phone" className="mb-1 block text-sm text-gray-400">
              Phone
            </label>
            <input
              id="account-phone"
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onBlur={() => {
                if (phone) setPhone(formatPhone(phone));
              }}
              className={inputClasses}
              placeholder="(555) 123-4567"
            />
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <span className="text-xs text-gray-500">
              {user?.tenant?.name} &middot; {user?.role}
            </span>
          </div>
        </form>
      </div>
    </div>
  );
}
